/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React, { useCallback, useState, useEffect } from 'react';

import { css, jsx } from '@compiled/react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { Popup } from '@atlaskit/editor-common/ui';
import { token } from '@atlaskit/tokens';

import type { AiSuggestionsPlugin } from '../../../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../../../pm-plugins/main';
import { updateSuggestionStatus } from '../../utils/api';
import { applySuggestion } from '../../utils/applySuggestion';

import { SuggestionCard } from './SuggestionCard';

const containerStyle = css({
	display: 'block',
	position: 'absolute',
	zIndex: 2,
	// Used for scrollIntoView so the card doesn't get cut off
	scrollMarginTop: token('space.300'),
});

/**
 * We render this in a content component because rendering directly in a Decoration widget
 * will cause it to inherit font stylings from Editor which is not appropriate for this component.
 */
export const SuggestionCardContentComponent = ({
	api,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
}): JSX.Element | undefined => {
	const scrollTargetIntoViewIfNeeded = useCallback((node: HTMLElement) => {
		const { top, bottom } = node.getBoundingClientRect();
		if (top < 0 || bottom > window.innerHeight) {
			node.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}, []);

	/**
	 * We use useState & useCallback to set the ref so that the popup will mount correctly after the
	 * anchor mounts.
	 */
	const [target, setTarget] = useState<HTMLSpanElement>();
	const [isShowingOnlySuggestion, setIsShowingOnlySuggestion] = useState(false);
	const targetRef = useCallback((node: HTMLSpanElement) => {
		setTarget(node);
	}, []);
	const {
		suggestionById,
		selectedSuggestion,
		baseLocalIdBySuggestionId,
		stackSize,
		orderedSuggestionIds,
	} = useSharedPluginStateWithSelector(api, ['aiSuggestions'], (state) => {
		const {
			suggestionById,
			suggestionIdsByBaseLocalId,
			baseLocalIdBySuggestionId,
			selectedSuggestion: selectedId,
			orderedSuggestionIds,
		} = state?.aiSuggestionsState ?? {};

		const selectedSuggestion =
			selectedId !== undefined && suggestionById !== undefined
				? suggestionById.get(selectedId)
				: undefined;

		const baseLocalId =
			baseLocalIdBySuggestionId && selectedId
				? baseLocalIdBySuggestionId.get(selectedId)
				: undefined;

		const stackSize = baseLocalId ? suggestionIdsByBaseLocalId?.get(baseLocalId)?.size : undefined;

		return {
			suggestionById,
			stackSize,
			selectedSuggestion,
			orderedSuggestionIds,
			baseLocalIdBySuggestionId,
		};
	});

	useEffect(() => {
		if (target && selectedSuggestion) {
			scrollTargetIntoViewIfNeeded(target);
		}
	}, [target, selectedSuggestion, scrollTargetIntoViewIfNeeded]);

	const onToggleOnlySuggestion = useCallback(() => {
		const nextIsShowingOnlySuggestion = !isShowingOnlySuggestion;
		setIsShowingOnlySuggestion(nextIsShowingOnlySuggestion);

		if (!selectedSuggestion) {
			return;
		}

		api?.aiSuggestions?.actions.showSuggestionDiff(selectedSuggestion.id, {
			hideDeletedDiffs: nextIsShowingOnlySuggestion,
		});
	}, [api, isShowingOnlySuggestion, selectedSuggestion]);

	if (selectedSuggestion === undefined) {
		return;
	}

	const onClose = () => {
		api?.core?.actions.execute(({ tr }) => {
			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'SELECT_SUGGESTION',
				id: undefined,
			});
			api?.showDiff?.commands.hideDiff?.({ tr });

			return tr;
		});
	};

	const onDiscard = () => {
		api?.core?.actions.execute(({ tr }) => {
			updateSuggestionStatus({
				id: selectedSuggestion.id,
				status: 'DISMISSED',
			});

			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'REMOVE_SUGGESTION',
				id: selectedSuggestion.id,
			});

			api?.showDiff?.commands.hideDiff?.({ tr });

			return tr;
		});
	};

	const onAccept = () => {
		updateSuggestionStatus({
			id: selectedSuggestion.id,
			status: 'ACTIONED',
		});

		api?.core?.actions.execute(({ tr }) => {
			api?.showDiff?.commands.hideDiff?.({ tr });
			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'REMOVE_SUGGESTION',
				id: selectedSuggestion.id,
			});
			return tr;
		});

		setTimeout(() => {
			applySuggestion({ api, suggestion: selectedSuggestion });
		}, 0);
	};

	const onNavigate = (direction: 'up' | 'down') => {
		const currentId = selectedSuggestion.id;
		if (!orderedSuggestionIds || orderedSuggestionIds?.length === 0) {
			return undefined;
		}
		const idx = orderedSuggestionIds.indexOf(currentId);
		if (idx === -1) {
			return undefined;
		}
		const delta = direction === 'up' ? -1 : 1;
		const nextIdx = (idx + delta + orderedSuggestionIds.length) % orderedSuggestionIds.length;
		const nextSuggestion = suggestionById?.get(orderedSuggestionIds[nextIdx]);
		if (!nextSuggestion) {
			return undefined;
		}
		api?.core?.actions.execute(({ tr }) => {
			api?.aiSuggestions?.actions.showSuggestionDiff(nextSuggestion.id, {
				hideDeletedDiffs: false,
			});
			const nextId = orderedSuggestionIds[nextIdx];

			if (nextId === undefined) {
				return tr;
			}

			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'SELECT_SUGGESTION',
				id: nextId,
			});
			return tr;
		});
	};

	/**
	 * We create an anchor that is positioned to match the anchor created in Decoration.widget.
	 * We also use a Popup and mount it onto this anchor so that we get the benefits of
	 * boundary detection and easier positioning.
	 */
	return (
		<>
			<span
				ref={targetRef}
				css={containerStyle}
				style={{
					positionAnchor: `--button-${baseLocalIdBySuggestionId?.get(selectedSuggestion.id)}`,
					insetBlockStart: `anchor(start)`,
					insetInlineStart: `anchor(end)`,
				}}
			/>
			<Popup target={target} stick={true} minPopupMargin={24}>
				<SuggestionCard
					suggestion={selectedSuggestion}
					onNavigate={
						orderedSuggestionIds && orderedSuggestionIds.length > 1 ? onNavigate : undefined
					}
					onClose={onClose}
					onDiscard={onDiscard}
					onAccept={onAccept}
					stackSize={stackSize}
					onToggleOnlySuggestion={onToggleOnlySuggestion}
					isShowingOnlySuggestion={isShowingOnlySuggestion}
				/>
			</Popup>
		</>
	);
};
